import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecipeVariants } from '@vanilla-extract/recipes';
import {
  checkboxBoxRecipe,
  checkboxWrapper,
  flexRowReverse,
  flexRow,
} from './checkbox.css';
import { faSquareCheck, faSquareMinus } from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { Typography, typographyRecipe } from '../typography/typography';
import { bgColorsVars } from '@design-system/vars.css';
import { clsx } from 'clsx';
import { useState } from 'react';

type CheckboxProps = {
  label?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange: (checked: boolean) => void;
  labelPosition?: 'left' | 'right';
  checkboxVariant?: RecipeVariants<typeof checkboxBoxRecipe>['variant'];
  textVariant?: RecipeVariants<typeof typographyRecipe>['variant'];
  textSize?: RecipeVariants<typeof typographyRecipe>['size'];
  textFontWeight?: RecipeVariants<typeof typographyRecipe>['fontWeight'];
  textColor?: keyof typeof bgColorsVars;
  disabled?: boolean;
  indeterminate?: boolean;
  iconClassName?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  testId?: string;
};

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  defaultChecked,
  indeterminate,
  onChange,
  labelPosition = 'right',
  checkboxVariant,
  disabled,
  textVariant,
  textSize,
  textColor,
  textFontWeight,
  iconClassName,
  labelClassName,
  wrapperClassName,
  testId,
}) => {
  const [internalChecked, setInternalChecked] = useState(
    defaultChecked || false,
  );
  const isControlled = checked !== undefined;
  const isChecked = isControlled ? checked : internalChecked;

  const handleClick = () => {
    if (disabled) return;
    const newChecked = !isChecked;
    if (!isControlled) {
      setInternalChecked(newChecked);
    }
    onChange?.(newChecked);
  };

  return (
    <div
      className={clsx(
        checkboxWrapper,
        labelPosition === 'left' && flexRowReverse,
        labelPosition !== 'left' && flexRow,
        wrapperClassName,
      )}
    >
      <div
        data-testid={testId || 'checkbox'}
        onClick={handleClick}
        className="flex items-center"
      >
        <FontAwesomeIcon
          icon={
            indeterminate && isChecked
              ? faSquareMinus
              : isChecked
              ? faSquareCheck
              : faSquare
          }
          className={clsx(
            checkboxBoxRecipe({ variant: checkboxVariant, disabled }),
            iconClassName,
          )}
        />
      </div>
      {label && (
        <Typography
          variant={textVariant}
          size={textSize}
          color={disabled ? 'N-60' : textColor}
          fontWeight={textFontWeight}
          className={clsx(labelClassName)}
        >
          {label}
        </Typography>
      )}
    </div>
  );
};
